export const DEFAULT_TILE_HEIGHT = 445;

/**
 * MUI spacing factor for `sx={{ pb: … }}` on scroll regions inside tiles.
 * Fixed tile `height` + inner `maxHeight` scroll areas need this so the last
 * row/card is not flush with the bottom edge of the scrollport.
 */
export const MY_PERF_SCROLL_LIST_PB = 3;

export const TYVASO_DPI_WIDGET_TYPES = [
  "tyvasoDpiDosageDistribution",
  "tyvasoDpiDosageNotification",
];

export const DEFAULT_LAYOUTS = {
  SALESREP: [
    {
      id: "sr-1",
      type: "startTracking",
      xs: 12,
      md: 6,
      height: DEFAULT_TILE_HEIGHT,
      switchable: true,
    },
    {
      id: "sr-2",
      type: "hcpGrowers",
      xs: 12,
      md: 6,
      height: DEFAULT_TILE_HEIGHT,
      switchable: true,
    },
    {
      id: "sr-3",
      type: "keyInsights",
      xs: 12,
      md: 6,
      height: DEFAULT_TILE_HEIGHT,
      switchable: true,
    },
    {
      id: "sr-4",
      type: "pendingReferrals",
      xs: 12,
      md: 6,
      height: DEFAULT_TILE_HEIGHT,
      switchable: true,
    },
  ],

  RBD: [
    {
      id: "rbd-1",
      height: DEFAULT_TILE_HEIGHT,
      type: "startTracking",
      xs: 12,
      md: 6,
      lg: 6,
      switchable: true,
    },
    {
      id: "rbd-2",
      height: DEFAULT_TILE_HEIGHT,
      type: "hcpGrowers",
      xs: 12,
      md: 6,
      lg: 6,
      switchable: true,
    },
    {
      id: "rbd-3",
      height: DEFAULT_TILE_HEIGHT,
      type: "regionalPerformance",
      xs: 12,
      md: 6,
      lg: 6,
      switchable: true,
    },
    {
      id: "rbd-4",
      height: DEFAULT_TILE_HEIGHT,
      type: "territoryPerformance",
      xs: 12,
      md: 6,
      lg: 6,
      switchable: true,
    },
  ],

  LEADERSHIP: [
    {
      id: "ld-1",
      type: "startTracking",
      xs: 12,
      md: 6,
      lg: 6,
      switchable: true,
      height: DEFAULT_TILE_HEIGHT,
    },
    {
      id: "ld-2",
      type: "hcpGrowers",
      xs: 12,
      md: 6,
      lg: 6,
      switchable: true,
      height: DEFAULT_TILE_HEIGHT,
    },
    {
      id: "ld-3",
      type: "regionTracking",
      xs: 12,
      md: 6,
      lg: 6,
      switchable: true,
      height: DEFAULT_TILE_HEIGHT,
    },
    {
      id: "ld-4",
      type: "kpiAlerts",
      xs: 12,
      md: 6,
      lg: 6,
      switchable: true,
      height: DEFAULT_TILE_HEIGHT,
    },
  ],
};

// which widgets are allowed to be swapped in a tile for each role
export const SWITCH_OPTIONS = {
  SALESREP: ["startTracking", "pendingReferrals", "keyInsights", "hcpGrowers"],
  RBD: [
    "regionalPerformance",
    "startTracking",
    "territoryPerformance",
    "hcpGrowers",
    "treatmentRate",
    "deNovo",
  ],
  LEADERSHIP: [
    "startTracking",
    "kpiAlerts",
    "regionTracking",
    "hcpGrowers",
    "treatmentRate",
    "deNovo",
  ],
};

// ---------------------------------------------------------------------------
// Sales rep — My Performance tab defaults (sectional `layout_scope` layouts).
// `order` matches API tile order (1-based); used when merging saved layouts.
// ---------------------------------------------------------------------------

/** API + Cosmos `layout_scope` values for rep My Performance tabs. */
export const SALESREP_MY_PERF_LAYOUT_SCOPES = {
  performance: "my_performance:performance",
  hcp: "my_performance:hcp",
  competitor: "my_performance:competitor",
};

export const SALESREP_MY_PERF_PERFORMANCE_LAYOUT = [
  {
    id: "mp-perf-1",
    type: "nationalPerformance",
    order: 1,
    xs: 12,
    md: 6,
    height: DEFAULT_TILE_HEIGHT,
    switchable: true,
  },
  {
    id: "mp-perf-2",
    type: "regionalTerritoryPerformance",
    order: 2,
    xs: 12,
    md: 6,
    height: DEFAULT_TILE_HEIGHT,
    switchable: true,
  },
  {
    id: "mp-perf-3",
    type: "pendingReferrals",
    order: 3,
    xs: 12,
    md: 6,
    height: DEFAULT_TILE_HEIGHT,
    switchable: true,
  },
  {
    id: "mp-perf-4",
    type: "reachAndFrequency",
    order: 4,
    xs: 12,
    md: 6,
    height: DEFAULT_TILE_HEIGHT,
    switchable: true,
  },
];

/** Explore HCPs: growers, physician behavior, UT engagement, patient trends (+ account changes via swap). */
export const SALESREP_MY_PERF_HCP_LAYOUT = [
  {
    id: "mp-hcp-1",
    type: "hcpGrowers",
    order: 1,
    xs: 12,
    md: 6,
    height: DEFAULT_TILE_HEIGHT,
    switchable: true,
  },
  {
    id: "mp-hcp-2",
    type: "insightPhysicianBehavior",
    order: 2,
    xs: 12,
    md: 6,
    height: DEFAULT_TILE_HEIGHT,
    switchable: true,
  },
  {
    id: "mp-hcp-3",
    type: "insightUtEngagement",
    order: 3,
    xs: 12,
    md: 6,
    height: DEFAULT_TILE_HEIGHT,
    switchable: true,
  },
  {
    id: "mp-hcp-4",
    type: "insightPatientTrends",
    order: 4,
    xs: 12,
    md: 6,
    height: DEFAULT_TILE_HEIGHT,
    switchable: true,
  },
];

export const SALESREP_MY_PERF_COMPETITOR_LAYOUT = [
  {
    id: "mp-comp-1",
    type: "insightCompetitorDynamics",
    order: 1,
    xs: 12,
    md: 6,
    height: DEFAULT_TILE_HEIGHT,
    switchable: true,
  },
  {
    id: "mp-comp-2",
    type: "topYutrepiaWriters",
    order: 2,
    xs: 12,
    md: 6,
    height: DEFAULT_TILE_HEIGHT,
    switchable: true,
  },
];

/** Default HCP tab widgets (one per tile). */
export const SALESREP_MY_PERF_HCP_SWITCH_OPTIONS = [
  "hcpGrowers",
  "insightPhysicianBehavior",
  "insightUtEngagement",
  "insightPatientTrends",
];

/** Optional HCP tab widgets — available via swap, not shown by default. */
export const SALESREP_MY_PERF_HCP_EXTRA_SWITCH_OPTIONS = [
  "hcpHcoAccountChanges",
  "deNovo",
];

/** Optional performance tab widgets — available via swap, not shown by default. */
export const SALESREP_MY_PERF_PERFORMANCE_EXTRA_SWITCH_OPTIONS = ["treatmentRate"];

export const SALESREP_MY_PERF_SWITCH_OPTIONS = {
  performance: [
    "nationalPerformance",
    "regionalTerritoryPerformance",
    "pendingReferrals",
    "reachAndFrequency",
  ],
  hcp: SALESREP_MY_PERF_HCP_SWITCH_OPTIONS,
  competitor: ["insightCompetitorDynamics", "topYutrepiaWriters"],
};
